import { useRef, useState } from "react";
import "./ReportFileSlot.css";
import { Button, Modal, NoticeBox, ProgressBar, Tags, Upload } from "../../../ui";
import { formatDate, openLudFile } from "../../utils/ludDocuments.helpers";
import { replaceLudReportFileAPI } from "../../../../utils/api/ludDocuments";

// Report-file slots (backend: services/reportFileService.js). A versioned entry is one PART of a
// slot (store + app + kind + month); it shows its part label, version and size, and can be replaced
// on its own. Entries uploaded before versioning ("legacy") stay downloadable but are not replaced.

const T = {
  en: {
    part: "Part",
    version: (n) => `Version ${n}`,
    legacy: "Before versioning",
    legacyHint: "Uploaded before versioning: it can be downloaded or deleted, not replaced.",
    download: "Download",
    replace: "Replace this part",
    title: "Replace this file part",
    current: "Current file",
    newFile: "New file",
    choose: "Choose the new file",
    warning: "After the replacement is saved, the previous file of this part is deleted and can no longer be downloaded. Other parts are not changed.",
    confirm: "Replace",
    cancel: "Cancel",
    close: "Close",
    uploading: "Uploading…",
    replaced: (n) => `Replaced — now version ${n}.`,
    identical: "The file is identical to the current one; nothing was changed.",
    cleanupPending: "The previous file will be removed shortly.",
    errors: {
      VERSION_CONFLICT: "Someone saved a newer version of this file. Reload the page to see it before replacing.",
      REPLACEMENT_IN_PROGRESS: "Another replacement of this file is running. Try again in a moment.",
      LEGACY_FILE_NOT_REPLACEABLE: "This file was uploaded before versioning and cannot be replaced yet.",
      UPLOAD_FAILED: "The file could not be uploaded. The current file was kept.",
      COMMIT_FAILED: "The replacement could not be saved. The current file was kept.",
      fallback: "The file could not be replaced. The current file was kept.",
    },
  },
  ar: {
    part: "الجزء",
    version: (n) => `الإصدار ${n}`,
    legacy: "قبل تفعيل الإصدارات",
    legacyHint: "رُفع قبل تفعيل الإصدارات: يمكن تنزيله أو حذفه، ولا يمكن استبداله.",
    download: "تنزيل",
    replace: "استبدال هذا الجزء",
    title: "استبدال جزء الملف",
    current: "الملف الحالي",
    newFile: "الملف الجديد",
    choose: "اختر الملف الجديد",
    warning: "بعد حفظ الاستبدال يُحذف الملف السابق لهذا الجزء ولن يكون متاحاً للتنزيل. الأجزاء الأخرى لا تتغير.",
    confirm: "استبدال",
    cancel: "إلغاء",
    close: "إغلاق",
    uploading: "جارٍ الرفع…",
    replaced: (n) => `تم الاستبدال — الإصدار الحالي ${n}.`,
    identical: "الملف مطابق للملف الحالي؛ لم يتغير شيء.",
    cleanupPending: "سيُحذف الملف السابق قريباً.",
    errors: {
      VERSION_CONFLICT: "قام شخص آخر بحفظ إصدار أحدث من هذا الملف. حدّث الصفحة لرؤيته قبل الاستبدال.",
      REPLACEMENT_IN_PROGRESS: "يوجد استبدال آخر لهذا الملف قيد التنفيذ. حاول بعد قليل.",
      LEGACY_FILE_NOT_REPLACEABLE: "رُفع هذا الملف قبل تفعيل الإصدارات ولا يمكن استبداله حالياً.",
      UPLOAD_FAILED: "تعذّر رفع الملف. تم الإبقاء على الملف الحالي.",
      COMMIT_FAILED: "تعذّر حفظ الاستبدال. تم الإبقاء على الملف الحالي.",
      fallback: "تعذّر استبدال الملف. تم الإبقاء على الملف الحالي.",
    },
  },
};

export const isVersionedReport = (report) => Boolean(report?.partKey && report?.fileVersion);

export const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

// Readable part name: "Part 2 · branch-b.xlsx" (number = creation order within the slot).
export const partLabelOf = (report, siblings = [], lang = "en") => {
  const text = T[lang] || T.en;
  const parts = siblings
    .filter((item) => isVersionedReport(item) && item.app === report.app && item.kind === report.kind && Number(item.year) === Number(report.year) && Number(item.month) === Number(report.month))
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt) || String(a.partKey).localeCompare(String(b.partKey)));
  const index = parts.findIndex((item) => item.partKey === report.partKey);
  const number = index >= 0 ? index + 1 : null;
  return [number ? `${text.part} ${number}` : text.part, report.partLabel].filter(Boolean).join(" · ");
};

export const fileNameOf = (report) => report?.currentFile?.uploadedName || report?.currentFile?.originalName || "";

export default function ReportFileSlot({ report, siblings = [], restaurant, token, lang = "en", canManage = false, onReplaced, onError }) {
  const text = T[lang] || T.en;
  const versioned = isVersionedReport(report);
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const busy = progress !== null;

  const download = () => openLudFile(report.currentFile, { token, storeId: restaurant.id, kind: "report", reference: report.id || report._id, onError });

  const choose = (event) => {
    const picked = event.target.files?.[0] || null;
    event.target.value = "";
    if (!picked) return;
    setFile(picked);
    setResult(null);
    setOpen(true);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    setFile(null);
  };

  const replace = async () => {
    if (!file || busy) return;
    setProgress(0);
    try {
      const response = await replaceLudReportFileAPI(token, restaurant.id, report.id || report._id, {
        file,
        expectedVersion: report.fileVersion,
        onProgress: setProgress,
      });
      setResult({
        variant: "success",
        message: response.noop ? text.identical : `${text.replaced(response.report?.fileVersion)}${response.cleanupPending ? ` ${text.cleanupPending}` : ""}`,
      });
      setOpen(false);
      setFile(null);
      if (!response.noop) await onReplaced?.(response.report);
    } catch (error) {
      setResult({ variant: "danger", message: text.errors[error?.data?.code] || text.errors.fallback });
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="lud-report-slot" data-testid={`report-slot-${report.id || report._id}`}>
      <div className="lud-report-slot__meta">
        {versioned ? (
          <>
            <span className="lud-report-slot__part">{partLabelOf(report, siblings, lang)}</span>
            <Tags variant="primary">{text.version(report.fileVersion)}</Tags>
          </>
        ) : (
          <Tags variant="neutral" title={text.legacyHint}>{text.legacy}</Tags>
        )}
        {report.currentFile?.size ? <span>{formatFileSize(report.currentFile.size)}</span> : null}
        {report.currentFile?.uploadedAt ? <span>{formatDate(report.currentFile.uploadedAt, lang)}</span> : null}
      </div>
      <div className="lud-report-slot__actions">
        <Button size="sm" appearance="soft" variant="primary" onClick={download}>{text.download}</Button>
        {canManage && versioned && (
          <>
            <Button size="sm" appearance="soft" variant="warning" icon={<Upload size={15} />} onClick={() => inputRef.current?.click()} disabled={busy}>
              {text.replace}
            </Button>
            <input
              ref={inputRef}
              type="file"
              className="lud-report-slot__input"
              accept=".pdf,.xls,.xlsx,.xlsm,.xlsb,.csv"
              aria-label={text.choose}
              onChange={choose}
            />
          </>
        )}
      </div>
      {result && !open && (
        <NoticeBox variant={result.variant} size="sm" className="lud-report-slot__result">
          <span role={result.variant === "danger" ? "alert" : "status"}>{result.message}</span>
        </NoticeBox>
      )}
      {open && (
        <Modal
          title={text.title}
          onClose={close}
          busy={busy}
          closeLabel={text.close}
          size="sm"
          footer={(
            <>
              <Button appearance="soft" onClick={close} disabled={busy}>{text.cancel}</Button>
              <Button variant="warning" appearance="filled" onClick={replace} loading={busy} disabled={busy}>{text.confirm}</Button>
            </>
          )}
        >
          <dl className="lud-report-slot__compare">
            <dt>{text.current}</dt>
            <dd>{fileNameOf(report)} — {partLabelOf(report, siblings, lang)} — {text.version(report.fileVersion)}</dd>
            <dt>{text.newFile}</dt>
            <dd>{file?.name} ({formatFileSize(file?.size)})</dd>
          </dl>
          <NoticeBox variant="warning" size="sm">{text.warning}</NoticeBox>
          {result?.variant === "danger" && (
            <NoticeBox variant="danger" size="sm">
              <span role="alert">{result.message}</span>
            </NoticeBox>
          )}
          {busy && (
            <div className="lud-report-slot__progress">
              <span>{text.uploading} {Math.round((progress || 0) * 100)}%</span>
              <ProgressBar value={(progress || 0) * 100} label={text.uploading} />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
